/**
 * testing-row-bits — the small shared pieces of a Testing row: element identity
 * (icon · name · tracker id), how long it has been under test, and how many
 * times it has been through the queue.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import type { QueueElement, QueuePoolSummary } from '@trafficpulse/shared';

import { Tooltip } from '@/components/ui';
import { cn, formatDateTimeGb } from '@/lib/utils';

import { layerIcon } from '../../../queue-icons';
import { layerLabel, testedForLabel } from '../../../queue-status.util';

/**
 * Icon · NAME · short tracker id. The name is what the user reads, so it gets
 * the room; the id is a disambiguator, shown as a short prefix (a full UUID
 * rendered `shrink-0` used to eat the column — truncating the name to nothing
 * and overflowing into the next column). The full id stays in the tooltip.
 */
export const ElementIdentity: FC<{ el: QueueElement; strong?: boolean }> = ({
  el,
  strong,
}) => {
  const Icon = layerIcon(el.layer);
  const shortId =
    el.itemId.length > 10 ? `${el.itemId.slice(0, 8)}…` : el.itemId;
  return (
    <>
      <Icon
        className={cn('h-3.5 w-3.5 shrink-0', strong ? 'text-primary' : 'text-muted-foreground')}
        aria-label={layerLabel(el.layer)}
      />
      <span
        className={cn('min-w-0 truncate', strong ? 'font-semibold' : 'font-medium')}
        title={el.itemName || undefined}
      >
        {el.itemName || 'Unnamed'}
      </span>
      <span
        className="shrink-0 font-mono text-[10px] text-muted-foreground"
        title={`Tracker id ${el.itemId}`}
      >
        #{shortId}
      </span>
    </>
  );
};

export const TestedFor: FC<{ el: QueueElement }> = ({ el }) => (
  <Tooltip content={el.testStartedAt ? `Live since ${formatDateTimeGb(el.testStartedAt)}` : null}>
    <span
      tabIndex={el.testStartedAt ? 0 : undefined}
      className="cursor-default rounded text-[11px] tabular-nums text-muted-foreground outline-none focus-visible:ring-1 focus-visible:ring-ring"
    >
      {testedForLabel(el.testStartedAt)}
    </span>
  </Tooltip>
);

/**
 * Quiet by design: a first-stint element says nothing. Only an element that has
 * been round the queue before earns a mark, because that is the surprising bit.
 */
export const RunsBadge: FC<{ el: QueueElement }> = ({ el }) =>
  el.timesQueued > 1 ? (
    <span
      className="shrink-0 rounded bg-muted px-1 text-[10px] font-medium tabular-nums text-muted-foreground"
      title={`Entered the queue ${el.timesQueued} times`}
    >
      ×{el.timesQueued}
    </span>
  ) : null;

/** "cap 2/2" for the inline group meta; destructive once over cap. */
export const CapMeter: FC<{ pool?: QueuePoolSummary }> = ({ pool }) => {
  if (!pool || pool.cap == null) return null;
  const over = pool.active > pool.cap;
  return (
    <span className={cn('tabular-nums', over && 'font-semibold text-destructive')}>
      cap {pool.active}/{pool.cap}
    </span>
  );
};
